import { CheckIcon } from '@heroicons/react/24/outline';
import type { PatientFile } from '../../../../services/cloud-files';
import { MAX_NOTE_BODY_LENGTH } from '../../../../components/patient/notes/noteAccess';
import { StarterScanSection } from './StarterScanSection';

type UploadMode = 'files' | 'scanId';

interface DeliverAlignersSectionProps {
    patientId: string | null;
    chartNotes: string;
    onChartNotes: (value: string) => void;
    ack: boolean;
    onAckChange: (ack: boolean) => void;
    docLabel?: string;
    /** True only for SDI deliver where alignerOption === 'Starter'. */
    isStarter?: boolean;
    /** Starter scan mode ('files' | 'scanId'). */
    scanMode?: UploadMode;
    onScanModeChange?: (mode: UploadMode) => void;
    /** Scan ID value when scanMode === 'scanId'. */
    scanId?: string;
    onScanIdChange?: (scanId: string) => void;
    /** Per-card file callbacks so index.tsx can derive filesReady. */
    onUpperFilesChange?: (files: PatientFile[]) => void;
    onLowerFilesChange?: (files: PatientFile[]) => void;
    onBiteFilesChange?: (files: PatientFile[]) => void;
    /** Bubbles upload-in-flight state up to the page for submit gating. */
    onUploadStateChange?: (isUploading: boolean, delta: 1 | -1) => void;
}

export function DeliverAlignersSection({
    patientId,
    chartNotes,
    onChartNotes,
    ack,
    onAckChange,
    docLabel = 'SDI',
    isStarter = false,
    scanMode = 'files',
    onScanModeChange,
    scanId = '',
    onScanIdChange,
    onUpperFilesChange,
    onLowerFilesChange,
    onBiteFilesChange,
    onUploadStateChange
}: DeliverAlignersSectionProps) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col flex-shrink-0 gap-4">
            <ChartNotesSection chartNotes={chartNotes} onChartNotes={onChartNotes} />

            {isStarter &&
                onScanModeChange &&
                onScanIdChange &&
                onUpperFilesChange &&
                onLowerFilesChange &&
                onBiteFilesChange && (
                    <>
                        <div className="h-px bg-gray-200" />
                        <StarterScanSection
                            patientId={patientId}
                            mode={scanMode}
                            onModeChange={onScanModeChange}
                            scanId={scanId}
                            onScanIdChange={onScanIdChange}
                            onUpperFilesChange={onUpperFilesChange}
                            onLowerFilesChange={onLowerFilesChange}
                            onBiteFilesChange={onBiteFilesChange}
                            onUploadStateChange={onUploadStateChange}
                        />
                    </>
                )}

            <div className="h-px bg-gray-200" />
            <AcknowledgementSection ack={ack} onAckChange={onAckChange} docLabel={docLabel} />
        </div>
    );
}

interface ChartNotesSectionProps {
    chartNotes: string;
    onChartNotes: (value: string) => void;
}

function ChartNotesSection({ chartNotes, onChartNotes }: ChartNotesSectionProps) {
    const atLimit = chartNotes.length >= MAX_NOTE_BODY_LENGTH;
    return (
        <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                Appointment chart notes
                <span className="text-gray-400 normal-case font-medium tracking-normal ml-1">
                    (Optional)
                </span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
                Please paste your appointment chart notes below. You can add chart notes in the
                patient notes section later.
            </p>
            <textarea
                value={chartNotes}
                onChange={(e) => onChartNotes(e.target.value)}
                maxLength={MAX_NOTE_BODY_LENGTH}
                rows={7}
                placeholder="Enter chart notes or SOAP notes for the appointment"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 resize-none"
            />
            <div
                className={`flex justify-end gap-2 mt-1 text-xs ${atLimit ? 'text-red-500' : 'text-gray-400'}`}
            >
                {atLimit && <span>Character limit reached. Longer text is cut off.</span>}
                <span>
                    {chartNotes.length}/{MAX_NOTE_BODY_LENGTH}
                </span>
            </div>
        </div>
    );
}

interface AcknowledgementSectionProps {
    ack: boolean;
    onAckChange: (ack: boolean) => void;
    docLabel: string;
}

function AcknowledgementSection({ ack, onAckChange, docLabel }: AcknowledgementSectionProps) {
    return (
        <div className="bg-gray-50 rounded-lg p-3">
            <button
                type="button"
                onClick={() => onAckChange(!ack)}
                className="flex items-start gap-2.5 cursor-pointer text-left"
            >
                <span
                    className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                        ack ? 'bg-yellow-400 border-yellow-500' : 'bg-white border-gray-400'
                    }`}
                    style={{ borderWidth: '1.5px', borderStyle: 'solid' }}
                >
                    {ack && <CheckIcon className="w-2.5 h-2.5 text-gray-900" strokeWidth={3} />}
                </span>
                <span className="text-xs text-gray-700 font-medium leading-snug">
                    I have read the {docLabel} and acknowledge all instructions given to me.
                </span>
            </button>
        </div>
    );
}
